/**
 * Bandwidth meter + reporter for StorySplat-hosted asset traffic.
 *
 * The legacy pipeline reported bandwidth exactly once per scene load — the
 * primary splat's byte count captured via `asset.on('progress')` and posted by
 * the `loaded`-event consumer (createViewerFromSceneId / the v2 HTML tracking
 * script / StorySplatV2Viewer). That misses everything else a scene downloads:
 * LOD chunks streamed after `loaded` fires (the majority of a LOD scene's
 * bytes), 4DGS frame sequences, secondary splats, GLB models, hotspot media,
 * and audio loaded through the PlayCanvas asset system.
 *
 * This module counts bytes at the network layer instead. It wraps
 * `window.fetch` and `XMLHttpRequest` once per page (PlayCanvas loads all
 * assets through XHR/fetch) and attributes completed responses to a scene when
 * the URL is StorySplat-hosted AND belongs to the scene owner's storage
 * subtree (`users/{ownerId}/…`).
 *
 * Byte size prefers the response's Resource Timing `transferSize` (the true
 * over-the-wire size we pay egress on — and, critically, `0` for responses the
 * browser served from its HTTP cache, which cost nothing). The worker sets
 * `Timing-Allow-Origin: *` so this is available for CDN-proxied traffic. When
 * Resource Timing is unavailable (cross-origin without a TAO header, e.g. a
 * direct-Firebase scene), it falls back to the `Content-Length` header (fetch)
 * or the XHR ProgressEvent's `loaded` count.
 *
 * Attribution is single-owner: each completed request is charged to at most ONE
 * active reporter, so N same-owner embeds on one page don't each count every
 * request N times. A reporter whose sceneId appears in the URL path wins; ties
 * fall to the most recently created reporter (the active scene).
 *
 * Reporting protocol (POST {baseUrl}/api/track-embed, type 'bandwidth'):
 *  - one report when the scene finishes loading (`initial: true` — the server
 *    counts it as a bandwidth-view, preserving viewCount ≈ loads),
 *  - a delta report every REPORT_INTERVAL_MS while streaming continues
 *    (`initial: false` — bytes only, no viewCount increment),
 *  - a final sendBeacon flush on pagehide/visibility-hidden/destroy.
 *
 * Known blind spots (documented, not bugs): media loaded via bare
 * `<img>/<video>/new Audio()` elements (skybox images, hotspot/portal video,
 * narration audio) bypasses fetch/XHR and is not counted client-side. Exact
 * per-scene egress requires server-side metering; this meter is the
 * best-effort client signal.
 */

import { safeRandomUUID } from './analyticsConsent';

/** @internal exported for unit tests */
export interface ReporterEntry {
    sceneId: string;
    ownerPathSegment: string; // `users/${ownerId}/` (decoded)
    ownerPathEncoded: string; // `users%2F${ownerId}%2F` (Firebase-encoded)
    isStorySplatHostedUrl: (url: string) => boolean;
    seq: number;              // creation order — newest wins attribution ties
    disposed: boolean;
    add: (bytes: number) => void;
}

const reporters = new Set<ReporterEntry>();
let reporterSeq = 0;
let interceptorsInstalled = false;

function decodeUrl(url: string): string {
    try {
        return decodeURIComponent(url);
    } catch {
        return url;
    }
}

/**
 * Resolve a completed response's billable byte size from Resource Timing, with a
 * Content-Length fallback. `0` for cache hits (nothing crossed the wire).
 * @internal exported for unit tests
 *   - transferSize > 0            → wire bytes (headers + body).
 *   - encodedBodySize > 0, ts = 0 → TAO present, served from cache → 0.
 *   - both 0                      → no TAO; use the captured Content-Length fallback.
 */
export function resolveMeteredBytes(
    transferSize: number | undefined,
    encodedBodySize: number | undefined,
    fallback: number | null,
): number {
    if (typeof transferSize === 'number' && transferSize > 0) return transferSize;
    if (typeof encodedBodySize === 'number' && encodedBodySize > 0) return 0; // cache hit
    return fallback ?? 0;
}

/**
 * Pick the single reporter a request's bytes belong to (or null). A request is
 * charged to at most ONE reporter so N same-owner embeds don't each count it.
 * A reporter whose sceneId is named in the URL wins; ties break to the most
 * recently created (active) scene.
 * @internal exported for unit tests
 */
export function selectReporterFor(url: string, entries: Iterable<ReporterEntry>): ReporterEntry | null {
    if (!url) return null;
    const decoded = decodeUrl(url);
    let best: ReporterEntry | null = null;
    let bestScore = 0;
    for (const entry of entries) {
        if (entry.disposed) continue;
        try {
            if (!entry.isStorySplatHostedUrl(url)) continue;
        } catch {
            continue;
        }
        const ownsIt = decoded.includes(entry.ownerPathSegment) || url.includes(entry.ownerPathEncoded);
        if (!ownsIt) continue;
        // Prefer a reporter whose scene is named in the URL (LOD `/sog/{sceneId}/`,
        // `/{sceneId}.ext`, or the worker's `ss_scene=` hint) over an owner-only
        // match; break ties toward the most recently created (active) scene.
        const sceneNamed = !!entry.sceneId
            && (decoded.includes(`/${entry.sceneId}/`)
                || decoded.includes(`/${entry.sceneId}.`)
                || url.includes(`ss_scene=${entry.sceneId}`));
        const score = sceneNamed ? 2 : 1;
        if (score > bestScore || (score === bestScore && best !== null && entry.seq > best.seq)) {
            best = entry;
            bestScore = score;
        }
    }
    return best;
}

// Content-Length / XHR-loaded fallbacks keyed by URL, drained by the Resource
// Timing observer. Only consulted when Timing-Allow-Origin is absent (e.g. a
// direct-Firebase scene) so the true wire size isn't visible to us.
const fallbackByUrl = new Map<string, number[]>();

function pushFallback(url: string, bytes: number): void {
    if (!url || !(bytes > 0)) return;
    const queue = fallbackByUrl.get(url);
    if (queue) {
        queue.push(bytes);
        if (queue.length > 8) queue.shift(); // bound per-URL (reused endpoints)
    } else {
        fallbackByUrl.set(url, [bytes]);
    }
    // Safety cap: failed requests never produce a Resource Timing entry to drain
    // their fallback, so clear wholesale if the map grows pathological.
    if (fallbackByUrl.size > 1000) fallbackByUrl.clear();
}

function takeFallback(url: string): number | null {
    const queue = fallbackByUrl.get(url);
    if (!queue || queue.length === 0) return null;
    const value = queue.shift() as number;
    if (queue.length === 0) fallbackByUrl.delete(url);
    return value;
}

/**
 * Resource Timing observer — the authoritative byte counter. An entry is
 * emitted at responseEnd (after the body fully arrives), so its `transferSize`
 * is the real over-the-wire cost and is naturally `0` for browser-cache hits.
 * This is why we count here and not at fetch's header-time resolve: crediting
 * Content-Length on `.then` billed aborted/cached loads at full size. It also
 * observes `<img>/<video>/Audio()` loads that bypass fetch/XHR entirely.
 *   - transferSize > 0            → wire bytes (headers + body).
 *   - encodedBodySize > 0, ts = 0 → TAO present, nothing on the wire → cache hit, 0.
 *   - both 0                      → no TAO; fall back to the Content-Length we captured.
 */
let perfObserver: PerformanceObserver | null = null;

function ensurePerfObserver(): void {
    if (perfObserver || typeof PerformanceObserver === 'undefined') return;
    try {
        perfObserver = new PerformanceObserver((list) => {
            if (reporters.size === 0) return;
            for (const raw of list.getEntries()) {
                if (raw.entryType !== 'resource') continue;
                const entry = raw as PerformanceResourceTiming;
                const fallback = takeFallback(entry.name); // always drain to avoid leaks
                const bytes = resolveMeteredBytes(entry.transferSize, entry.encodedBodySize, fallback);
                if (bytes > 0) dispatch(entry.name, bytes);
            }
        });
        // `buffered: true` replays entries recorded just before we subscribed
        // (e.g. the primary splat if it beat us), so nothing early is missed.
        perfObserver.observe({ type: 'resource', buffered: true });
    } catch {
        perfObserver = null; // Resource Timing observer unavailable — fallbacks below still count
    }
}

/** Charge a completed request to exactly one active reporter (or none). */
function dispatch(url: string, bytes: number): void {
    if (!url || !Number.isFinite(bytes) || bytes <= 0 || reporters.size === 0) return;
    const best = selectReporterFor(url, reporters);
    if (best) {
        try {
            best.add(bytes);
        } catch {
            // Accounting must never break the request path.
        }
    }
}

/**
 * Install the Resource Timing observer + fetch/XHR Content-Length fallback
 * capture once. The wrappers stay installed for the page lifetime (unpatching
 * is unsafe if anything else also wrapped the globals); they are inert no-ops
 * while no reporter is registered. Counting happens in the observer, not here —
 * the interceptors only stash a Content-Length fallback for no-TAO responses.
 */
function installInterceptors(): void {
    if (interceptorsInstalled || typeof window === 'undefined') return;
    interceptorsInstalled = true;

    ensurePerfObserver();

    try {
        const originalFetch = window.fetch.bind(window);
        window.fetch = function interceptedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
            const result = originalFetch(input as RequestInfo, init);
            if (reporters.size === 0) return result;
            let requestUrl = '';
            try {
                requestUrl = typeof input === 'string'
                    ? input
                    : input instanceof URL
                        ? input.href
                        : (input as Request).url || '';
            } catch { /* leave empty */ }
            if (!requestUrl) return result;
            return result.then((response) => {
                try {
                    // Stash Content-Length as a fallback the observer uses only when
                    // Resource Timing can't see the transfer (no TAO). Captured at
                    // header-time, which is BEFORE the entry's responseEnd, so it's
                    // always available when the observer drains it.
                    const contentLength = Number(response.headers.get('content-length'));
                    if (Number.isFinite(contentLength) && contentLength > 0) {
                        pushFallback(response.url || requestUrl, contentLength);
                    }
                } catch { /* never break the response path */ }
                return response;
            });
        };
    } catch {
        // Read-only fetch (locked-down environment) — fetch fallback won't be
        // captured, but the observer still counts TAO'd traffic.
    }

    try {
        const xhrProto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
        if (!xhrProto) return;
        type MeteredXhr = XMLHttpRequest & { __storysplatMeterUrl?: string };
        const originalOpen = xhrProto.open;
        const originalSend = xhrProto.send;
        xhrProto.open = function interceptedOpen(this: MeteredXhr, ...args: unknown[]) {
            try {
                const url = args[1];
                this.__storysplatMeterUrl = typeof url === 'string' ? url : (url instanceof URL ? url.href : '');
            } catch { /* leave unset */ }
            return (originalOpen as (...a: unknown[]) => void).apply(this, args);
        } as typeof xhrProto.open;
        xhrProto.send = function interceptedSend(this: MeteredXhr, ...args: unknown[]) {
            if (reporters.size > 0 && this.__storysplatMeterUrl) {
                const requestUrl = this.__storysplatMeterUrl;
                try {
                    // `once` so a reused XHR object (open→send→open→send) doesn't
                    // accumulate listeners. Stash a Content-Length (or loaded)
                    // fallback for the observer's no-TAO path.
                    this.addEventListener('loadend', (event: ProgressEvent) => {
                        try {
                            const headerLength = Number(this.getResponseHeader('content-length'));
                            const fallback = Number.isFinite(headerLength) && headerLength > 0
                                ? headerLength
                                : (event.loaded || 0);
                            if (fallback > 0) pushFallback(this.responseURL || requestUrl, fallback);
                        } catch { /* never break the XHR */ }
                    }, { once: true });
                } catch { /* never break the XHR */ }
            }
            return (originalSend as (...a: unknown[]) => void).apply(this, args);
        } as typeof xhrProto.send;
    } catch {
        // XHR not patchable — fetch interception above still counts fetch traffic.
    }
}

export interface BandwidthReporterOptions {
    baseUrl: string;
    sceneId: string;
    ownerId: string;
    /** Host allowlist check shared with the viewer (createViewer's isStorySplatHostedUrl). */
    isStorySplatHostedUrl: (url: string) => boolean;
}

export interface BandwidthReporterHandle {
    /** Cumulative StorySplat-hosted bytes observed for this scene so far. */
    getTotalBytes(): number;
    /** Signal that the scene finished its initial load — sends the `initial: true` report. */
    markLoaded(): void;
    /** Final beacon flush + stop counting/reporting. Safe to call more than once. */
    dispose(): void;
}

const REPORT_INTERVAL_MS = 60_000;
// Don't spend a request on a trivial mid-session delta…
const MIN_INTERVAL_REPORT_BYTES = 256 * 1024;
// …but flush smaller remainders when the page is going away.
const MIN_BEACON_REPORT_BYTES = 16 * 1024;
// Stay under the edge's 500MB per-request cap; remainder goes next tick.
const MAX_REPORT_BYTES = 400 * 1024 * 1024;

/** @internal exported so retry/idempotency semantics can be unit tested. */
export interface PendingBandwidthReport {
    reportId: string;
    reportSequence: number;
    bytes: number;
    initial: boolean;
}

/**
 * Serial report state. `next()` returns the exact same object until it is
 * acknowledged, so a timeout, retry, or overlapping pagehide beacon cannot
 * change the payload bound to a reportId/sequence pair.
 * @internal exported for unit tests
 */
export function createBandwidthReportSequencer(reportId = safeRandomUUID()) {
    let totalBytes = 0;
    let acknowledgedBytes = 0;
    let nextSequence = 0;
    let pending: PendingBandwidthReport | null = null;

    return {
        add(bytes: number): void {
            if (Number.isFinite(bytes) && bytes > 0) totalBytes += bytes;
        },
        getTotalBytes(): number {
            return totalBytes;
        },
        next(minBytes: number): PendingBandwidthReport | null {
            if (pending) return pending;
            const delta = Math.min(totalBytes - acknowledgedBytes, MAX_REPORT_BYTES);
            if (delta <= 0 || delta < minBytes) return null;
            pending = {
                reportId,
                reportSequence: nextSequence,
                bytes: delta,
                initial: nextSequence === 0,
            };
            return pending;
        },
        acknowledge(report: PendingBandwidthReport): void {
            if (pending !== report) return;
            acknowledgedBytes += report.bytes;
            nextSequence++;
            pending = null;
        },
    };
}

export function createBandwidthReporter(options: BandwidthReporterOptions): BandwidthReporterHandle {
    const { baseUrl, sceneId, ownerId, isStorySplatHostedUrl } = options;
    const endpoint = `${baseUrl}/api/track-embed`;

    const reportState = createBandwidthReportSequencer();
    let loadedSeen = false;
    let sendInFlight = false;
    let disposed = false;
    let permanentlyRejected = false;

    // Registered in the module-global attribution registry. Only count objects
    // in the scene owner's storage subtree — this keeps two different creators'
    // embeds on one page from cross-attributing bytes, and excludes shared
    // platform assets under public/…. Firebase URLs encode the path
    // (users%2F{uid}%2F…); the Cloudflare proxy keeps that encoding.
    const entry: ReporterEntry = {
        sceneId,
        ownerPathSegment: `users/${ownerId}/`,
        ownerPathEncoded: `users%2F${ownerId}%2F`,
        isStorySplatHostedUrl,
        seq: reporterSeq++,
        disposed: false,
        add: (bytes: number) => {
            if (!disposed) reportState.add(bytes);
        },
    };

    installInterceptors();
    reporters.add(entry);

    const buildPayload = (report: PendingBandwidthReport): string => JSON.stringify({
        sceneId,
        ownerId,
        type: 'bandwidth',
        bytes: report.bytes,
        initial: report.initial,
        reportId: report.reportId,
        reportSequence: report.reportSequence,
    });

    const sendViaFetch = async (): Promise<void> => {
        if (sendInFlight || permanentlyRejected) return;
        const report = reportState.next(1);
        if (!report) return;
        sendInFlight = true;
        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: buildPayload(report),
                // Survive page teardown: a tab-close right after the initial
                // report must not abort it (which lost the load's bytes AND its
                // view for every fast-exit visitor).
                keepalive: true,
            });
            if (res.ok) {
                reportState.acknowledge(report);
            } else if (!(res.status >= 500 || res.status === 429 || res.status === 408)) {
                // A permanent rejection means subsequent sequence numbers would
                // be gaps too. Stop this reporter rather than hammering the edge.
                permanentlyRejected = true;
            }
        } catch {
            // Network error: keep the exact pending report for the next retry.
        } finally {
            sendInFlight = false;
        }
    };

    const sendViaBeacon = (minBytes: number): void => {
        if (permanentlyRejected) return;
        const report = reportState.next(minBytes);
        if (!report) return;
        try {
            // text/plain avoids the CORS preflight sendBeacon can't perform;
            // the endpoint parses JSON bodies regardless of content type.
            // A successful sendBeacon only means the browser queued the body;
            // it is not an acknowledgement. Keep this exact report pending. If
            // the page survives, the next fetch safely retries the same pair.
            navigator.sendBeacon(endpoint, new Blob([buildPayload(report)], { type: 'text/plain' }));
        } catch { /* beacon unavailable — nothing to do on page exit */ }
    };

    const intervalId = setInterval(() => {
        if (disposed) return;
        // Hold periodic reports until the initial (view-counting) report went
        // out with the loaded signal, so a slow first load doesn't get counted
        // as a view by an interval firing first — unless the scene never
        // reaches 'loaded' (load failure): after the first interval, report
        // what was consumed anyway so failed loads still attribute egress.
        if (!loadedSeen) {
            loadedSeen = true; // one grace interval, then report on the next
            return;
        }
        const report = reportState.next(MIN_INTERVAL_REPORT_BYTES);
        if (report) void sendViaFetch();
    }, REPORT_INTERVAL_MS);

    const handleVisibility = (): void => {
        if (document.visibilityState === 'hidden') sendViaBeacon(MIN_BEACON_REPORT_BYTES);
    };
    const handlePageHide = (): void => sendViaBeacon(MIN_BEACON_REPORT_BYTES);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handlePageHide);

    return {
        getTotalBytes: () => reportState.getTotalBytes(),
        markLoaded: () => {
            if (disposed) return;
            loadedSeen = true;
            void sendViaFetch();
        },
        dispose: () => {
            if (disposed) return;
            sendViaBeacon(1); // flush any remainder, however small
            disposed = true;
            entry.disposed = true;
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('pagehide', handlePageHide);
            reporters.delete(entry);
        },
    };
}
